import { PillButton } from "@/components/Button/PillButton";
import { DialogBox } from "@/components/DialogBox/DialogBox";
import { CloseSvg } from "@/components/Icon/Icon";
import { CASE_TABLE_STATE_NAME } from "@/constants/designTableConstants";
import { CaseReadPublic, useMarkCaseAsRejectedApiV1PutMutation } from "@/store/baseClientApi";
import { clientApi } from "@/store/clientApi";
import { selectCheckedIds, selectRtkData } from "@/store/slices/tableSlice";
import { RootState } from "@/store/store";
import { Box, Divider, TextField, Typography } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";

const emptyCases: CaseReadPublic[] = [];
const rejectableIdsSelector = createSelector(
  [
    (state: RootState) => selectRtkData(state, CASE_TABLE_STATE_NAME, clientApi.endpoints.getCasesApiV1CasesGet.select)?.data?.content,
    (state: RootState) => selectCheckedIds(state, CASE_TABLE_STATE_NAME),
    (state: RootState, selectedCase: string[] | undefined) => selectedCase
  ],
  (content, checkedIds, selectedCase) => {
    if (selectedCase !== undefined) {
      checkedIds = selectedCase;
    }

    const dentalCases = checkedIds
      ? checkedIds
          .map((id) => content?.find((dentalCase) => dentalCase.id.toString() === id))
          .filter((dentalCase): dentalCase is CaseReadPublic => dentalCase !== undefined && dentalCase.id > 0)
      : emptyCases;

    const casesMarkedAsDone = dentalCases
      .filter((dentalCase) => dentalCase.status === "done")
      .map((dentalCase) => dentalCase.id.toString());

    if (dentalCases.length !== casesMarkedAsDone.length) {
      return emptyCases;
    }

    return casesMarkedAsDone;
  },
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual
    }
  }
);

export const RejectPillButton = ({ selectedCases }: { selectedCases?: string[] }) => {
  const { t } = useTranslation("cases");

  const casesMarkedAsDone = useSelector((state: RootState) => rejectableIdsSelector(state, selectedCases));
  const canRejectCases = casesMarkedAsDone.length > 0;
  const [markAsRejected] = useMarkCaseAsRejectedApiV1PutMutation();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const rejectedForInputRef = useRef<HTMLInputElement>();

  const handleClick = () => {
    setShowRejectDialog(true);
  };

  const handleMarkAsRejected = async () => {
    const rejectedFor = rejectedForInputRef.current?.value;
    setShowRejectDialog(false);

    if (casesMarkedAsDone.length === 0) {
      return;
    }
    const loadingToast = toast.loading(t("label.markingCasesAsRejected", { count: casesMarkedAsDone.length }));
    let failedCount = 0;
    const deletionPromises = casesMarkedAsDone.map(async (caseId) => {
      try {
        markAsRejected({
          caseId: Number(caseId),
          markAsRejectedRequest: {
            rejected_for: rejectedFor
          }
        });
      } catch (_) {
        failedCount++;
      }
    });
    await Promise.allSettled(deletionPromises);

    if (failedCount > 0) {
      toast.error(t("error.failedToMarkAsRejected", { count: failedCount }));
    }

    toast.dismiss(loadingToast);
  };

  return canRejectCases ? (
    <>
      <PillButton onClick={handleClick} startIcon={<CloseSvg color="inherit" />} color="error">
        <Typography fontSize="inherit" fontWeight="inherit" noWrap>
          {t("common:button.reject")}
        </Typography>
      </PillButton>
      <DialogBox
        args={undefined}
        title={t("title.rejectDesign")}
        sx={{ ".MuiPaper-root": { width: "100%", maxWidth: "800px", margin: "16px" } }}
        message={
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Typography>{t("message.markCasesAsRejected")}</Typography>
            <Divider />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography>{t("message.rejectedReason")}</Typography>
              <TextField
                sx={{
                  margin: "9px 16px 0px 16px"
                }}
                multiline
                minRows={2}
                inputRef={rejectedForInputRef}
              />
            </Box>
          </Box>
        }
        open={showRejectDialog}
        setOpen={setShowRejectDialog}
        onClose={(approve) => {
          if (approve) {
            handleMarkAsRejected();
          }
        }}
        confirmColor="error"
        confirmText={t("common:button.reject")}
        declineText={t("common:button.cancel")}
      />
    </>
  ) : (
    <></>
  );
};
